// İSG uzmanı portalı — Kontrol itemı oluştur/düzenle formu (Modal + RHF + Zod). api-contract §25.1:
// `POST /control-items` (create) / `PUT /control-items/{uuid}` (update). Alanlar: metin (`text`,
// zorunlu — kontrol ifadesi/sorusu), açıklama (opsiyonel), ve düzenlemede **aktif/pasif**. **`isDefault`
// istemciden GÖNDERİLMEZ** (genel öge de düzenlenebilir/pasifleştirilebilir — rozet yalnız görünüm).
//
// KKD formunun (ProtectiveEquipmentFormModal) muadili; tek fark alan adı (`text` vs `name`) + kanon
// §25. Başarı sonrası katalog listesi + tip-formu seçicisi (options) invalidate.

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  type ControlItemDto,
  Field,
  Input,
  Modal,
  Spinner,
  controlItemsApi,
  resolveApiErrorMessage,
} from '@isgportal/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { controlItemsKeys } from '../../lib/controlItemsKeys';

interface ControlItemFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Verilirse düzenleme; yoksa oluşturma. */
  item?: ControlItemDto | null;
  onSuccess?: (message: string) => void;
}

/** Boş/whitespace dizeyi `null`'a indirger (opsiyonel alan backend'e `null` gider). */
function nullable(value: string | undefined): string | null {
  return value && value.trim().length > 0 ? value.trim() : null;
}

export function ControlItemFormModal({
  open,
  onOpenChange,
  item,
  onSuccess,
}: ControlItemFormModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isEdit = Boolean(item);
  const [isActive, setIsActive] = useState(true);

  const schema = useMemo(
    () =>
      z.object({
        text: z.string().trim().min(1, t('permitTypes.catalog.control.validation.textRequired')),
        description: z.string().trim().optional(),
      }),
    [t],
  );
  type FormValues = z.infer<typeof schema>;

  const { register, handleSubmit, reset, formState } = useForm<FormValues>({
    resolver: zodResolver(schema),
    values: {
      text: item?.text ?? '',
      description: item?.description ?? '',
    },
  });

  // Düzenlemede aktiflik durumunu seed et; oluşturmada aktif başlat. `open` kapanınca sıfırlanır.
  useEffect(() => {
    if (!open) {
      return;
    }
    setIsActive(isEdit ? (item?.isActive ?? true) : true);
  }, [open, isEdit, item]);

  const mutation = useMutation({
    mutationFn: (values: FormValues) => {
      // **`isDefault` GÖNDERİLMEZ** (§25.1) — yalnız metin/açıklama(/aktiflik).
      const base = { text: values.text.trim(), description: nullable(values.description) };
      if (item) {
        return controlItemsApi.updateControlItem(item.uuid, { ...base, isActive });
      }
      return controlItemsApi.createControlItem(base);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: controlItemsKeys.list() });
      queryClient.invalidateQueries({ queryKey: controlItemsKeys.options() });
      reset();
      onSuccess?.(
        item ? t('permitTypes.catalog.control.updated') : t('permitTypes.catalog.control.created'),
      );
      onOpenChange(false);
    },
  });

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          mutation.reset();
          reset();
        }
        onOpenChange(next);
      }}
      title={
        isEdit
          ? t('permitTypes.catalog.control.form.edit.title')
          : t('permitTypes.catalog.control.form.create.title')
      }
      description={t('permitTypes.catalog.control.form.description')}
      size="md"
      closeLabel={t('permitTypes.common.close')}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t('permitTypes.common.cancel')}
          </Button>
          <Button type="submit" form="control-item-form" loading={mutation.isPending}>
            {mutation.isPending ? (
              <Spinner label={t('permitTypes.common.save')} />
            ) : isEdit ? (
              t('permitTypes.common.save')
            ) : (
              t('permitTypes.common.create')
            )}
          </Button>
        </>
      }
    >
      <form
        id="control-item-form"
        className="flex flex-col gap-5"
        onSubmit={handleSubmit((values) => mutation.mutate(values))}
        noValidate
      >
        {mutation.isError ? (
          <Alert tone="danger">{resolveApiErrorMessage(mutation.error, t)}</Alert>
        ) : null}

        <Field
          htmlFor="control-item-text"
          label={t('permitTypes.catalog.control.form.field.text')}
          error={formState.errors.text?.message}
          required
        >
          <Input
            id="control-item-text"
            invalid={Boolean(formState.errors.text)}
            {...register('text')}
          />
        </Field>

        <Field
          htmlFor="control-item-description"
          label={t('permitTypes.catalog.control.form.field.description')}
        >
          <Input id="control-item-description" {...register('description')} />
        </Field>

        {/* Aktif/pasif — yalnız düzenlemede (yeni öge aktif başlar; §25). Genel öge de pasifleştirilebilir. */}
        {isEdit ? (
          <label className="flex items-center gap-2 text-sm text-fg">
            <input
              type="checkbox"
              className="h-4 w-4 accent-primary"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            <span className="flex flex-col">
              <span className="font-medium">{t('permitTypes.catalog.form.field.isActive')}</span>
              <span className="text-xs text-muted">
                {t('permitTypes.catalog.form.isActiveHint')}
              </span>
            </span>
          </label>
        ) : null}
      </form>
    </Modal>
  );
}
